import React, { useCallback, useState } from 'react';
import './adminLogin.css';
import {
    ADMIN_LOGIN_ENDPOINT,
    ADMIN_LOGIN_FALLBACK_ENDPOINT,
    persistAdminSession,
} from './adminAuth';

const shouldIncludeCredentials = (url) => {
    if (typeof window === 'undefined') return false;
    try {
        return new URL(url, window.location.origin).origin === window.location.origin;
    } catch (error) {
        return false;
    }
};

const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch (error) {
        return { raw: text };
    }
};

const pickToken = (payload) => {
    if (!payload || typeof payload !== 'object') return '';
    const keys = [
        payload.token,
        payload?.auth?.token,
        payload?.user?.auth?.token,
        payload.access_token,
        payload.accessToken,
        payload?.auth?.access_token,
        payload?.auth?.accessToken,
        payload?.user?.auth?.access_token,
        payload?.user?.auth?.accessToken,
        payload.admin_token,
        payload.adminToken,
        payload?.auth?.admin_token,
        payload?.auth?.adminToken,
        payload?.user?.auth?.admin_token,
        payload?.user?.auth?.adminToken,
        payload.bearer_token,
        payload.bearerToken,
        payload?.auth?.bearer_token,
        payload?.auth?.bearerToken,
        payload?.user?.auth?.bearer_token,
        payload?.user?.auth?.bearerToken,
        payload?.data?.token,
        payload?.data?.access_token,
        payload?.data?.accessToken,
        payload?.data?.admin_token,
        payload?.data?.adminToken,
    ];
    const match = keys.find((value) => String(value || '').trim());
    return String(match || '').trim();
};

const AdminLoginPage = ({ onSuccess, notice = '' }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();

        const normalizedUsername = String(username || '').trim();
        if (!normalizedUsername || !password) {
            setErrorMessage('Enter username and password.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const requestBody = {
                username: normalizedUsername,
                password,
            };
            const loginRequest = (url) => fetch(url, {
                method: 'POST',
                credentials: shouldIncludeCredentials(url) ? 'include' : 'omit',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            let response = await loginRequest(ADMIN_LOGIN_ENDPOINT);
            if (
                (response.status === 404 || response.status === 405)
                && ADMIN_LOGIN_FALLBACK_ENDPOINT
                && ADMIN_LOGIN_FALLBACK_ENDPOINT !== ADMIN_LOGIN_ENDPOINT
            ) {
                response = await loginRequest(ADMIN_LOGIN_FALLBACK_ENDPOINT);
            }

            const payload = await parseJsonResponse(response);
            if (!response.ok) {
                const message = payload?.error || payload?.message || response.statusText || 'Login failed';
                throw new Error(`${response.status}: ${message}`);
            }

            // Only an admin account gets in (and gets the admin API's token from the login); an older backend
            // that sends no role is let through, as before.
            const role = String(payload?.user?.role || '').trim();
            if (role && role !== 'admin') {
                throw new Error('This account has no admin access.');
            }
            persistAdminSession(pickToken(payload));
            setPassword('');
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } catch (error) {
            setErrorMessage(error.message || 'Login failed.');
        } finally {
            setIsSubmitting(false);
        }
    }, [onSuccess, password, username]);

    return (
        <div className="admin-login-page">
            <section className="admin-login-card" aria-label="Admin login">
                <div className="admin-login-header">
                    <h1>Admin Login</h1>
                    <p>Sign in to continue to the admin panel.</p>
                </div>

                <form className="admin-login-form" onSubmit={handleSubmit}>
                    <label className="admin-login-field">
                        <span>Username or Email</span>
                        <input
                            type="text"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            autoComplete="username"
                            disabled={isSubmitting}
                        />
                    </label>

                    <label className="admin-login-field">
                        <span>Password</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            disabled={isSubmitting}
                        />
                    </label>

                    {errorMessage ? (
                        <p className="admin-login-error">{errorMessage}</p>
                    ) : notice ? (
                        <p className="admin-login-error">{notice}</p>
                    ) : null}

                    <button type="submit" className="admin-login-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Logging in...' : 'Login'}
                    </button>
                </form>
            </section>
        </div>
    );
};

export default AdminLoginPage;
